// A2F — silently persist the in-progress "Add New IND" form (modules/fre/FrePage)
// so a full-page SharePoint/Azure reconnect (or any navigate-away-and-back within
// the tab) doesn't erase what the user typed.
//
// Only the meaningful user inputs are persisted — not discovered folders
// (re-derived from the root path / SPO selection), the locked system-core ignore
// set (server-fetched constant), or transient UI flags (saving/ingesting/error).
// sessionStorage carries it across the OAuth redirect; a 24h TTL stops a
// genuinely stale tab from resurrecting old content while still bridging the
// reconnect (which takes seconds). Storage access is try/catch-wrapped and
// failures are swallowed, mirroring lib/ActiveOrgContext.
//
// IMPORTANT — drift risk. TypeScript forces every FreDraft field to appear in
// both saveFreDraft's caller (FrePage builds a full FreDraft literal) and
// loadFreDraft's return literal, so a forgotten field in either is a compile
// error. What the compiler does NOT check: (1) that a new persisted useState in
// modules/fre/FrePage.tsx was actually added to this interface, and (2) that
// each normalizer line below applies a real runtime type-guard rather than a
// blind `as` cast. Those two are the hand-kept invariants.

import { createLogger } from './debug';
import type { SPOSelection } from '@/lib/graphApi';

const log = createLogger('fre-draft');

const KEY = 'chrona_fre_draft';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

export interface FreDraft {
  projectName: string;
  indNumber: string;
  rootPath: string;
  spoSelection: SPOSelection | null;
  ignoreFolders: string[];
  activeFolders: string[];
  rulesExpanded: boolean;
  // Whether the convention ignore-defaults had been seeded into ignoreFolders at
  // save time. Lets restore distinguish "user cleared the chips" (seeded:true,
  // ignoreFolders:[]) from "defaults hadn't loaded yet" (seeded:false) so we
  // don't permanently suppress the recommended defaults — see FrePage seededRef.
  seeded: boolean;
  // The storage mode (SPO vs local) the draft was authored in. isSPOMode() is a
  // deploy-time constant, but a draft carried across a mode change (or a tampered
  // blob) is discarded on restore rather than half-applied — see FrePage.
  isSPO: boolean;
}

interface StoredDraft extends FreDraft {
  savedAt: number;
}

/**
 * Validate an untrusted value as an SPOSelection (the required string fields per
 * lib/graphApi.ts). Unlike the other draft fields, SPOSelection is owned by
 * another module and is fed to the Graph API on restore, so a corrupt blob must
 * not pass through — this keeps the normalizer's enforcement uniform.
 */
function isSPOSelection(v: unknown): v is SPOSelection {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as Record<string, unknown>;
  return (
    typeof s.siteId === 'string' &&
    typeof s.siteUrl === 'string' &&
    typeof s.siteName === 'string' &&
    typeof s.driveId === 'string' &&
    typeof s.driveName === 'string' &&
    // folderId / folderName are optional, but if present must be strings — a
    // non-string folderId would flow to spo_folder_id and the Graph item path.
    (s.folderId === undefined || typeof s.folderId === 'string') &&
    (s.folderName === undefined || typeof s.folderName === 'string')
  );
}

/** Load a non-expired draft, normalizing each field. Returns null if absent, expired, or malformed. */
export function loadFreDraft(): FreDraft | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    // Storage unavailable (private mode / disabled) — expected, not a defect.
    return null;
  }
  if (!raw) return null;

  try {
    // Untrusted blob — every field below is re-validated / normalized.
    const d = JSON.parse(raw) as Partial<StoredDraft>;
    if (typeof d?.savedAt !== 'number' || Date.now() - d.savedAt > TTL_MS) {
      clearFreDraft();
      return null;
    }
    return {
      projectName: typeof d.projectName === 'string' ? d.projectName : '',
      indNumber: typeof d.indNumber === 'string' ? d.indNumber : '',
      rootPath: typeof d.rootPath === 'string' ? d.rootPath : '',
      spoSelection: isSPOSelection(d.spoSelection) ? d.spoSelection : null,
      ignoreFolders: Array.isArray(d.ignoreFolders) ? d.ignoreFolders : [],
      activeFolders: Array.isArray(d.activeFolders) ? d.activeFolders : [],
      rulesExpanded: d.rulesExpanded === true,
      seeded: d.seeded === true,
      isSPO: d.isSPO === true,
    };
  } catch (err) {
    // Corrupt JSON / shape change — distinct from storage-unavailable above. Log
    // (debug-gated, zero prod noise) so a future FreDraft/SPOSelection shape
    // regression that quietly blanks restored fields is debuggable.
    log.warn('failed to parse FRE draft, discarding', err);
    return null;
  }
}

export function saveFreDraft(draft: FreDraft): void {
  try {
    const payload: StoredDraft = { ...draft, savedAt: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable / quota — drafting is best-effort */
  }
}

export function clearFreDraft(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Restore a draft only if it was authored in the current storage mode (SPO vs
 * local). A cross-mode draft (mode flipped between deploys, or a tampered blob)
 * is dropped rather than half-applied — the form mounts empty and the caller's
 * empty-form save then clears the stale blob.
 */
export function selectDraftForMode(draft: FreDraft | null, isSPO: boolean): FreDraft | null {
  return draft && draft.isSPO === isSPO ? draft : null;
}

/**
 * Whether the user has entered something worth persisting. ignoreFolders is
 * deliberately excluded: it's pre-seeded with the convention defaults, so a
 * non-empty ignore list is not by itself evidence the user started filling the
 * form (and including it would auto-persist the defaults and resurrect them next
 * visit — the bug the `seeded` flag exists to prevent).
 */
export function isFreFormMeaningful(
  // The Pick is the enforcement point for "ignoreFolders is excluded": it isn't
  // a parameter, so a pre-seeded ignore list cannot, by construction, make the
  // form look meaningful and auto-persist the defaults.
  d: Pick<FreDraft, 'projectName' | 'indNumber' | 'rootPath' | 'spoSelection' | 'activeFolders'>,
): boolean {
  return (
    d.projectName.trim() !== '' ||
    d.indNumber.trim() !== '' ||
    d.rootPath.trim() !== '' ||
    d.spoSelection != null ||
    d.activeFolders.length > 0
  );
}
